import type { ActionId, WeaponId } from '@cratered/shared';
import { PRICES, RECRUIT_PRICES, isPurchasable, type PurchasableId } from './economy';
import { clamp } from './rng';
import {
  STAMINA, UNIT_HALF_W, WALK_SPEED, platformSpot, teleportSpot, type Catapult, type Sim, type SimTeam, type Unit, UNIT_H,
} from './sim';
import { ACTIONS, CATAPULT_CUP_H, CATAPULT_REACH, PLACE_REACH, STRIKE_REACH, TELEPORT_REACH, launchSpeed, type ActionDef } from './weapons';

interface Plan {
  unitId: string;
  stage: 'think' | 'walk' | 'attack' | 'aim' | 'fire' | 'done';
  t: number;
  walkDir: -1 | 0 | 1;
  walkTicks: number;
  action: ActionId;
  angle: number;
  power: number;
  /** Which way to face for the attack: toward the target, whichever way the walk went. */
  facing: 1 | -1;
  aimStart: number;
  aimPower: number;
  /** Where the mole stood at the last stall check, to notice a wall. */
  lastX: number;
  /** A teleport (or a catapult ride) to take before anything else this turn (it takes the move), aimed from where the mole stands now. */
  blink: { action: 'teleport' | 'catapult'; angle: number; power: number } | null;
  /** The attack was chosen from somewhere else (before a walk or a blink): choose again once the mole is back on its feet. */
  rethink: boolean;
  /** How many refused attacks have been swapped for another this turn, so the bot does not insist forever. */
  retries: number;
}

/**
 * Brawl: what one bot-driven mole is up to between decisions. There is no turn to plan around,
 * so this is only a walk direction, two timers and the bookkeeping that stops a bot insisting
 * on an attack the sim keeps turning down.
 */
interface LiveState {
  /** Ticks until the next movement decision. */
  moveAt: number;
  walkDir: -1 | 0 | 1;
  /** Ticks until the next attack is considered (counted alongside the sim's own cooldown). */
  fireAt: number;
  /** Where the mole stood at the last movement decision, to notice a wall. */
  lastX: number;
  /** Attacks the sim refused since the last one that went: not chosen again until something lands. */
  refused: Set<ActionId>;
}

/** What to do from where we stand; `good` is false when the best option still expects to miss. */
interface Attack {
  action: ActionId;
  angle: number;
  power: number;
  facing: 1 | -1;
  good: boolean;
}

export type BotStyle = 'balanced' | 'rusher' | 'sniper' | 'bomber' | 'camper';
export const BOT_STYLES: readonly BotStyle[] = ['balanced', 'rusher', 'sniper', 'bomber', 'camper'];

export interface BotOptions {
  /** Balance testing: shop for and attack with this weapon only (skip when it runs out). */
  onlyWeapon?: WeaponId;
  /** Balance testing: never hire allies. */
  noRecruits?: boolean;
  /**
   * Personality for map testing: rushers close in and boot, snipers buy rifles and hold lines of
   * sight, bombers stock grenades, mortars and RPGs, campers never walk.
   */
  style?: BotStyle;
  /** Map testing: act for this team only, so two controllers with different personalities can share a match. */
  team?: number;
}

/**
 * Runs on the host for raider units and absent players. In the buy phase it shops for every
 * bot-controlled team; on a turn it thinks for a moment, maybe walks toward the fight (or over to
 * a catapult, or blinks halfway there), then boots a neighbour, shoots a visible target, calls
 * the sky down on one, or lobs something along a searched trajectory.
 */
export class BotController {
  private plan: Plan | null = null;
  private shopped = new Set<number>();
  private shopDelay = 0;
  /** Attacks the sim turned down this turn (a catapult we drifted from, say): not chosen again until the next plan. */
  private refused = new Set<ActionId>();
  /** Brawl: one state per mole the bot drives, keyed by unit id. */
  private live = new Map<string, LiveState>();

  constructor(private readonly difficulty: number, private readonly rng: () => number, private readonly options: BotOptions = {}) {}

  update(sim: Sim): void {
    const t = sim.turn;
    if (sim.ended) return;
    if (t.phase === 'buy') {
      this.shop(sim);
      return;
    }
    this.shopped.clear();
    if (sim.settings.mode === 'brawl') {
      this.brawl(sim);
      return;
    }
    if (!sim.isBotTurn() || (t.phase !== 'select' && t.phase !== 'move' && t.phase !== 'aim')) {
      if (t.phase === 'resolve' || t.phase === 'between') this.plan = null;
      return;
    }
    const unit = sim.activeUnit();
    if (!unit) return;
    if (this.options.team !== undefined && unit.team !== this.options.team) return;
    if (!this.plan || this.plan.unitId !== unit.id) this.plan = this.makePlan(sim, unit);
    const plan = this.plan;
    plan.t--;
    switch (plan.stage) {
      case 'think':
        if (plan.t > 0) break;
        if (t.phase === 'select' && !t.moved) {
          // Instant gear first: it takes the move, so a patched-up mole stays put and attacks.
          const gear = this.chooseGear(sim, unit);
          if (gear) {
            sim.applyInput(unit.playerId, { kind: 'action', action: gear }, true);
            plan.walkTicks = 0;
          } else if (plan.blink && this.blink(sim, unit, plan)) {
            // A blink takes the move too; the attack is chosen again from wherever the mole lands.
            plan.walkTicks = 0;
            plan.rethink = true;
          }
        }
        if (plan.walkTicks > 0 && t.phase === 'select' && !t.moved && !unit.floatie) {
          sim.applyInput(unit.playerId, { kind: 'action', action: 'move' }, true);
          sim.applyInput(unit.playerId, { kind: 'move', dir: plan.walkDir }, true);
          plan.stage = 'walk';
          plan.t = plan.walkTicks;
        } else {
          plan.stage = 'attack';
          plan.t = plan.rethink ? 12 : 0;
        }
        break;
      case 'walk':
        if (plan.t <= 0 || t.phase !== 'move' || (unit.onGround && this.cliffAhead(sim, unit, plan.walkDir))) {
          sim.applyInput(unit.playerId, { kind: 'move', dir: 0 }, true);
          sim.applyInput(unit.playerId, { kind: 'stopMove' }, true);
          // The attack is chosen once the mole has stopped and settled, not from mid-stride.
          plan.stage = 'attack';
          plan.t = 12;
          plan.rethink = true;
        } else if (unit.onGround && plan.t % 12 === 0) {
          // Not getting anywhere (a rock, a crate, a crater lip): hop and push on.
          const stalled = Math.abs(unit.x - plan.lastX) < 4;
          plan.lastX = unit.x;
          if (stalled || this.blocked(sim, unit, plan.walkDir)) {
            sim.applyInput(unit.playerId, { kind: 'move', dir: plan.walkDir }, true);
            sim.applyInput(unit.playerId, { kind: 'jump' }, true);
          }
        }
        break;
      case 'attack': {
        // Wait for the menu, and (within reason) for the mole's feet: a hop or a blink may still be in the air.
        if (plan.t > 0 || t.phase !== 'select' || (!unit.onGround && plan.t > -90)) break;
        if (plan.rethink) {
          plan.rethink = false;
          this.adopt(plan, this.chooseAttack(sim, unit));
        }
        if (plan.action === 'skip') {
          sim.applyInput(unit.playerId, { kind: 'skip' }, true);
          plan.stage = 'done';
          break;
        }
        sim.applyInput(unit.playerId, { kind: 'move', dir: plan.facing }, true);
        sim.applyInput(unit.playerId, { kind: 'action', action: plan.action }, true);
        if (ACTIONS[plan.action].kind === 'melee') {
          plan.stage = 'done';
          plan.t = 30;
          break;
        }
        // Read the phase afresh: the action input just changed it (or, if refused, did not).
        if (sim.turn.phase !== 'aim') {
          // Refused (a catapult we have drifted from, say): pick something else, once or twice, then give up.
          if (plan.retries++ < 2) {
            this.refused.add(plan.action);
            plan.rethink = true;
            plan.t = 4;
          } else {
            plan.stage = 'done';
            plan.t = 10;
          }
          break;
        }
        plan.stage = 'aim';
        plan.t = 40;
        plan.aimStart = unit.aim;
        plan.aimPower = 0.3;
        break;
      }
      case 'aim': {
        if (t.phase !== 'aim') {
          plan.stage = 'done';
          plan.t = 30;
          break;
        }
        const k = 1 - clamp(plan.t / 40, 0, 1);
        sim.applyInput(unit.playerId, {
          kind: 'aim', angle: plan.aimStart + (plan.angle - plan.aimStart) * k, power: plan.aimPower + (plan.power - plan.aimPower) * k,
        }, true);
        if (plan.t <= 0) {
          plan.stage = 'fire';
          plan.t = 8;
        }
        break;
      }
      case 'fire':
        if (plan.t > 0) break;
        if (ACTIONS[plan.action].kind === 'hitscan') sim.applyInput(unit.playerId, { kind: 'shoot', angle: plan.angle }, true);
        else sim.applyInput(unit.playerId, { kind: 'fire', angle: plan.angle, power: plan.power }, true);
        plan.stage = 'done';
        plan.t = 60;
        break;
      case 'done':
        // Still our turn after a moment? Something was refused; skip rather than idle.
        if (plan.t <= 0 && (t.phase === 'select' || t.phase === 'aim')) {
          sim.applyInput(unit.playerId, { kind: 'skip' }, true);
        }
        break;
    }
  }

  private adopt(plan: Plan, attack: Attack): void {
    plan.action = attack.action;
    plan.angle = attack.angle;
    plan.power = attack.power;
    plan.facing = attack.facing;
  }

  // ---------- brawl ----------

  /**
   * Brawl: every away player's mole is driven at once, each on its own clock. Nothing here reads
   * the shared turn state (there is none in a brawl beyond the round clock). The per-mole states
   * are dropped between rounds, so the next round starts from a fresh decision.
   */
  private brawl(sim: Sim): void {
    if (sim.turn.phase !== 'live' || !sim.isBotTurn()) {
      this.live.clear();
      return;
    }
    const alive = new Set<string>();
    for (const unit of sim.botUnits()) {
      if (this.options.team !== undefined && unit.team !== this.options.team) continue;
      // A player's inputs steer their one live mole; a hired ally only stands in (until it is the live one).
      if (sim.liveUnit(unit.playerId) !== unit) continue;
      alive.add(unit.id);
      let state = this.live.get(unit.id);
      if (!state) {
        // Spread the first decisions out so a crowd of bots does not all step off on the same tick.
        state = { moveAt: Math.floor(this.rng() * 20), walkDir: 0, fireAt: this.fireDelay(), lastX: unit.x, refused: new Set() };
        this.live.set(unit.id, state);
      }
      this.driveLive(sim, unit, state);
    }
    for (const id of this.live.keys()) if (!alive.has(id)) this.live.delete(id);
  }

  /** Ticks between two attacks on top of the sim's cooldown: a hard bot fires about as soon as it may, an easy one dawdles. */
  private fireDelay(): number {
    return Math.floor(40 + (1 - this.difficulty) * 40 + this.rng() * 10);
  }

  private driveLive(sim: Sim, unit: Unit, state: LiveState): void {
    state.moveAt--;
    state.fireAt--;
    const footing = unit.onGround && !unit.ragdoll && !unit.pull && !unit.floatie;
    // A cliff stops the walk the moment it shows, whatever the next decision would have said.
    if (state.walkDir !== 0 && footing && this.cliffAhead(sim, unit, state.walkDir)) this.walkLive(sim, unit, state, 0);
    if (state.moveAt <= 0) {
      state.moveAt = 25 + Math.floor(this.rng() * 21);
      this.decideWalk(sim, unit, state);
    } else if (state.walkDir !== 0 && footing && state.moveAt % 12 === 0 && this.blocked(sim, unit, state.walkDir)) {
      // A rock, a crate, a crater lip in the way: hop and push on.
      sim.applyInput(unit.playerId, { kind: 'jump' }, true);
    }
    // Only from a footing (or a floatie): a throw from mid-hop goes who knows where.
    if (unit.cooldown > 0 || state.fireAt > 0 || !(unit.onGround || unit.floatie) || unit.ragdoll || unit.pull) return;
    state.fireAt = this.fireDelay();
    this.attackLive(sim, unit, state);
  }

  /**
   * Which way to walk for the next while: toward the nearest enemy until it is in the preferred
   * range (rushers want to be on top of it, the rest a comfortable lob away), away from one that
   * has got too close (rushers stand their ground and swing), never over a cliff. Campers only
   * ever back off. A walk that has not got anywhere since the last decision, or one with
   * something in the way, gets a hop.
   */
  private decideWalk(sim: Sim, unit: Unit, state: LiveState): void {
    const target = this.nearestEnemy(sim, unit);
    const footing = unit.onGround && !unit.ragdoll && !unit.pull && !unit.floatie;
    let dir: -1 | 0 | 1 = 0;
    if (target && footing) {
      const style = this.options.style ?? 'balanced';
      const dist = Math.hypot(target.x - unit.x, target.y - unit.y);
      const toward: 1 | -1 = target.x < unit.x ? -1 : 1;
      const near = style === 'rusher' ? 200 : 700;
      if (dist > near) dir = style === 'camper' ? 0 : toward;
      else if (dist < 150 && style !== 'rusher') dir = toward === 1 ? -1 : 1;
      if (dir !== 0 && this.cliffAhead(sim, unit, dir)) dir = 0;
    }
    const stalled = state.walkDir !== 0 && dir === state.walkDir && Math.abs(unit.x - state.lastX) < 4;
    state.lastX = unit.x;
    this.walkLive(sim, unit, state, dir);
    if (dir !== 0 && footing && (stalled || this.blocked(sim, unit, dir))) sim.applyInput(unit.playerId, { kind: 'jump' }, true);
  }

  private walkLive(sim: Sim, unit: Unit, state: LiveState, dir: -1 | 0 | 1): void {
    state.walkDir = dir;
    sim.applyInput(unit.playerId, { kind: 'move', dir }, true);
  }

  /**
   * The mole may use something: gear first when it is worth it (a medkit when hurt), else the
   * attack chooseAttack likes from here, sent as the sim's live inputs: face the target, take
   * the thing in hand, fire it, then carry on walking the way the last decision said. The sim
   * sets the cooldown when a thing goes; when it does not (no ammo after all, nobody in reach of
   * the swing), that attack is set aside and another is tried soon. Catapult rides and teleports
   * are never chosen here: chooseAttack does not pick them, and a brawl is simpler without.
   */
  private attackLive(sim: Sim, unit: Unit, state: LiveState): void {
    const pid = unit.playerId;
    const gear = this.chooseGear(sim, unit);
    if (gear) {
      sim.applyInput(pid, { kind: 'action', action: gear }, true);
      if (unit.cooldown > 0) return;
    }
    this.refused.clear();
    for (const a of state.refused) this.refused.add(a);
    const attack = this.chooseAttack(sim, unit);
    this.refused.clear();
    if (attack.action === 'skip') {
      // Nothing worth throwing from here (or nobody to throw at): look again shortly.
      state.fireAt = Math.min(state.fireAt, 20);
      return;
    }
    sim.applyInput(pid, { kind: 'move', dir: attack.facing }, true);
    if (attack.action === 'boot') {
      sim.applyInput(pid, { kind: 'kick' }, true);
    } else {
      sim.applyInput(pid, { kind: 'action', action: attack.action }, true);
      const kind = ACTIONS[attack.action].kind;
      if (kind === 'hitscan') {
        sim.applyInput(pid, { kind: 'shoot', angle: attack.angle }, true);
      } else if (kind === 'throw') {
        sim.applyInput(pid, { kind: 'aim', angle: attack.angle, power: attack.power }, true);
        sim.applyInput(pid, { kind: 'fire', angle: attack.angle, power: attack.power }, true);
      }
    }
    // Facing the target set the mole walking that way; back to the walk the last decision chose.
    sim.applyInput(pid, { kind: 'move', dir: state.walkDir }, true);
    if (unit.cooldown > 0) {
      state.refused.clear();
    } else {
      state.refused.add(attack.action);
      state.fireAt = Math.min(state.fireAt, 15);
    }
  }

  // ---------- buy phase ----------

  private shop(sim: Sim): void {
    for (const team of sim.teams) {
      if (!sim.isBotControlled(team) || team.ready || this.shopped.has(team.team)) continue;
      if (this.options.team !== undefined && team.team !== this.options.team) continue;
      // Give humans a beat to see the bots arrive, then shop in one go.
      if (this.shopDelay++ < 45) return;
      const pid = team.playerIds[0] ?? 'ai';
      this.spend(sim, team, pid);
      sim.applyInput(pid, { kind: 'ready' }, true);
      this.shopped.add(team.team);
    }
  }

  private spend(sim: Sim, team: SimTeam, pid: string): void {
    const buy = (w: WeaponId, n: number) => {
      if (n > 0) sim.applyInput(pid, { kind: 'buy', weapon: w, count: n }, true);
    };
    const only = this.options.onlyWeapon;
    if (only) {
      // Balance runs: never let a one-weapon bot run dry mid-fight.
      if (isPurchasable(only)) for (let i = 0; i < 3; i++) buy(only, Math.min(20, Math.floor(team.coins / PRICES[only])));
      return;
    }
    // Allies first when flush, then a spread by personality.
    const style = this.options.style ?? 'balanced';
    if (!this.options.noRecruits && sim.recruitSlots(team) > 0 && !sim.liveTeams().every((x) => x === team)) {
      const want = team.coins >= RECRUIT_PRICES.boat + PRICES.grenade * 3 && this.rng() < (style === 'rusher' ? 0.7 : 0.5)
        ? 'boat'
        : team.coins >= RECRUIT_PRICES.floatie + PRICES.grenade * 2 && this.rng() < 0.6 ? 'floatie' : null;
      if (want && !sim.recruitBlocker(team, want)) sim.applyInput(pid, { kind: 'recruit', unit: want }, true);
    }
    // A taste of the wider arsenal by personality, then the staples. Every pick leaves `spare`
    // in the purse so the staples below still get bought: nobody blows the lot on one thing.
    const water = sim.level.waterY !== null;
    const fancy = (price: number, chance: number, spare = PRICES.grenade * 2): boolean => team.coins >= price + spare && this.rng() < chance;
    if (style === 'bomber') {
      // The big one only with real money behind it: a bomber with 550 still has a bag of grenades afterwards.
      if (fancy(PRICES.massive, 0.5, 150)) buy('massive', 1);
      if (fancy(PRICES.sticky, 0.7)) buy('sticky', 1);
      if (fancy(PRICES.bignade, 1)) buy('bignade', 1);
      if (fancy(PRICES.cluster, 0.6)) buy('cluster', 1);
    }
    if (style === 'sniper') {
      if (fancy(PRICES.railgun, 0.7, PRICES.sniper)) buy('railgun', 1);
      if (fancy(PRICES.crossbow, 0.6, PRICES.sniper)) buy('crossbow', 1);
      if (water && fancy(PRICES.harpoon, 0.5, PRICES.sniper)) buy('harpoon', 1);
    }
    if (style === 'rusher') {
      if (fancy(PRICES.smg, 0.7, PRICES.grenade)) buy('smg', 1);
      if (fancy(PRICES.teleport, 0.5, PRICES.grenade)) buy('teleport', 1);
      if (fancy(PRICES.shotgun, 0.8, PRICES.grenade)) buy('shotgun', 1);
      if (fancy(PRICES.bat, 0.7, PRICES.grenade)) buy(water ? 'bat' : 'mallet', 1);
    }
    if (style === 'camper') {
      if (fancy(PRICES.airstrike, 0.7)) buy('airstrike', 1);
      if (fancy(PRICES.mine, 0.5)) buy('mine', 1);
      if (fancy(PRICES.sandbags, 0.6)) buy('sandbags', 1);
    }
    if (style === 'balanced') {
      // One pick from the newer arsenal, so a balanced crew fields a little of everything over a match.
      const picks: PurchasableId[] = water
        ? ['sticky', 'railgun', 'smg', 'airstrike', 'crossbow']
        : ['sticky', 'rubber', 'railgun', 'smg', 'airstrike', 'crossbow'];
      const pick = picks[Math.floor(this.rng() * picks.length)] ?? 'sticky';
      if (fancy(PRICES[pick], 0.6)) buy(pick, 1);
    }
    if (fancy(PRICES.mine, style === 'camper' ? 0.7 : 0.35)) buy('mine', 1);
    const grenadeShare = style === 'bomber' ? 0.8 : style === 'sniper' ? 0.25 : 0.6;
    buy('grenade', Math.min(8, Math.floor((team.coins * grenadeShare) / PRICES.grenade)));
    const sniperShare = style === 'sniper' ? 0.9 : style === 'bomber' ? 0.2 : 0.7;
    buy('sniper', Math.min(style === 'sniper' ? 7 : 4, Math.floor((team.coins * sniperShare) / PRICES.sniper)));
    if (team.coins >= PRICES.rpg * (style === 'bomber' ? 1 : 2)) buy('rpg', style === 'bomber' ? 2 : 1);
    if (style === 'bomber' && team.coins >= PRICES.mortar + PRICES.grenade) buy('mortar', 1);
    if (team.coins >= PRICES.medkit + PRICES.grenade && this.rng() < 0.3) buy('medkit', 1);
    buy('grenade', Math.min(4, Math.floor(team.coins / PRICES.grenade)));
  }

  /** Gear worth using before anything else this turn: a medkit when hurt, a repair on a holed raft, cover for campers. */
  private chooseGear(sim: Sim, unit: Unit): ActionId | null {
    if (this.options.onlyWeapon) return null;
    const team = sim.teamOf(unit.team);
    const ammo = (w: WeaponId) => team?.ammo[w] ?? 0;
    if (ammo('medkit') > 0 && unit.hp < unit.maxHp * 0.45) return 'medkit';
    if (ammo('repair') > 0 && sim.repairTarget(unit)) return 'repair';
    if (ammo('sandbags') > 0 && (this.options.style ?? 'balanced') === 'camper' && this.visibleEnemy(sim, unit) && this.rng() < 0.6) return 'sandbags';
    return null;
  }

  // ---------- turns ----------

  /** Ideal flat-ground range of a full-power throw on this level (what an airstrike or a placed thing reaches, for those). */
  private reachOf(sim: Sim, unit: Unit, weapon: ActionId): number {
    const def = ACTIONS[weapon];
    if (def.strike) return STRIKE_REACH;
    if (def.place) return PLACE_REACH;
    if (def.kind !== 'throw' || def.speed <= 0) return 600;
    const mult = sim.teamOf(unit.team)?.perks.throwMultiplier ?? 1;
    const v = launchSpeed(def, 1, mult);
    return (v * v) / (def.gravity * sim.level.gravity);
  }

  /** Nothing to land on within a hop ahead: the next few steps would be a real fall, not a step down. */
  private cliffAhead(sim: Sim, unit: Unit, dir: number): boolean {
    const x = unit.x + dir * 26;
    for (let dy = 2; dy <= 110; dy += 6) if (sim.isSolid(x, unit.y + dy)) return false;
    return true;
  }

  private blocked(sim: Sim, unit: Unit, dir: number): boolean {
    for (let dy = 8; dy < UNIT_H; dy += 8) if (sim.isSolid(unit.x + dir * 20, unit.y - dy)) return true;
    return false;
  }

  private nearestEnemy(sim: Sim, unit: Unit): Unit | null {
    const enemies = sim.units.filter((u) => u.alive && u.team !== unit.team);
    enemies.sort((a, b) => Math.hypot(a.x - unit.x, a.y - unit.y) - Math.hypot(b.x - unit.x, b.y - unit.y));
    return enemies[0] ?? null;
  }

  /** A loaded catapult a short, level walk away: worth standing by before the target gets any closer. */
  private catapultNearby(sim: Sim, unit: Unit): Catapult | null {
    let best: Catapult | null = null;
    for (const c of sim.catapults) {
      const dx = Math.abs(c.x - unit.x);
      if (c.boulders <= 0 || dx > 320 || Math.abs(c.y - unit.y) > 60) continue;
      if (!best || dx < Math.abs(best.x - unit.x)) best = c;
    }
    return best;
  }

  private makePlan(sim: Sim, unit: Unit): Plan {
    this.refused.clear();
    const think = 30 + Math.floor(this.rng() * 40);
    const target = this.nearestEnemy(sim, unit);
    const base: Plan = {
      unitId: unit.id, stage: 'think', t: think, walkDir: 0, walkTicks: 0, action: 'skip', angle: 0.6, power: 0.6, facing: unit.facing,
      aimStart: 0.6, aimPower: 0.5, lastX: unit.x, blink: null, rethink: false, retries: 0,
    };
    if (!target) return base;
    const dist = Math.hypot(target.x - unit.x, target.y - unit.y);
    const dir: 1 | -1 = target.x < unit.x ? -1 : 1;
    // Walking is free, so close in until the preferred weapon can actually reach, or until a
    // hitscan has a line of sight (the railgun needs none). Campers never walk; rushers always do.
    const only = this.options.onlyWeapon;
    const style = this.options.style ?? 'balanced';
    const team = sim.teamOf(unit.team);
    const ammo = (w: WeaponId) => team?.ammo[w] ?? 0;
    const throwable: ActionId = only && ACTIONS[only].kind === 'throw' ? only : ammo('grenade') > 0 ? 'grenade' : 'ball';
    const wantsLos = only ? ACTIONS[only].kind === 'hitscan' && !ACTIONS[only].pierce : style === 'sniper';
    const noLos = wantsLos && this.visibleEnemy(sim, unit) === null;
    const reach = this.reachOf(sim, unit, throwable) * 0.85;
    // A short-range gun (balance runs with only a shotgun, say) means getting in close.
    const onlyRange = only ? ACTIONS[only].range : undefined;
    const wantsCloser = style === 'rusher' ? dist > 260 : onlyRange ? dist > onlyRange * 0.8 : dist > reach;
    // Nothing from here will connect (a mound in the way, a deck that rolls grenades off): move.
    const attack = this.chooseAttack(sim, unit);
    const stuck = !attack.good;
    this.adopt(base, attack);
    base.walkDir = dir;
    const far = dist > 300;
    // A loaded catapult is the big gun: beside one, stay and wind it; a short walk from one, go
    // and stand by it instead of marching off toward the target.
    const station = only ? null : sim.catapultFor(unit);
    const nearby = only || station ? null : this.catapultNearby(sim, unit);
    const canWalk = !unit.floatie && !sim.bootTarget(unit);
    if (canWalk && nearby && far && (style !== 'rusher' || this.rng() < 0.5)) {
      const toward: 1 | -1 = nearby.x < unit.x ? -1 : 1;
      if (!this.cliffAhead(sim, unit, toward)) {
        base.walkDir = toward;
        base.walkTicks = Math.ceil(Math.max(0, Math.abs(nearby.x - unit.x) - CATAPULT_REACH * 0.5) / WALK_SPEED) + 4;
      }
    } else if (canWalk && !(station && far)) {
      const willWalk = style === 'camper' ? stuck && this.rng() < 0.3 : wantsCloser || noLos || stuck;
      if (willWalk && this.rng() < 0.75 + this.difficulty * 0.25 && !this.cliffAhead(sim, unit, dir)) {
        base.walkDir = dir;
        // Most of the stamina budget, with a little variation so bots don't march in lockstep.
        const budget = Math.floor((STAMINA * sim.level.stamina) / WALK_SPEED);
        base.walkTicks = Math.max(30, Math.floor(budget * (0.6 + this.rng() * 0.35)));
      }
    }
    // Stuck with a teleport in the bag: blink halfway there instead (it takes the move), then look again from there.
    if (stuck && !only && ammo('teleport') > 0 && this.rng() < 0.7) {
      const aim = this.blinkAim(sim, unit, target, dir);
      if (aim) base.blink = { action: 'teleport', ...aim };
    }
    // Beside a catapult with launches left and the fight out of reach: ride it over, then attack from there.
    if (!only && station && (stuck || (far && wantsCloser)) && this.rng() < 0.85) {
      const ride = this.rideAim(sim, unit, target, dir, station);
      if (ride) {
        base.blink = { action: 'catapult', ...ride };
        base.walkDir = dir;
        base.walkTicks = 0;
      }
    }
    return base;
  }

  /**
   * Take the planned blink. Faces the target first (the aim's reach runs the way the mole
   * faces), and backs out of the aim if the sim refuses the spot, so the turn carries on as
   * planned. True when the mole went, in which case the turn sits in its gear move until it lands.
   */
  private blink(sim: Sim, unit: Unit, plan: Plan): boolean {
    const aim = plan.blink;
    if (!aim) return false;
    sim.applyInput(unit.playerId, { kind: 'move', dir: plan.facing }, true);
    sim.applyInput(unit.playerId, { kind: 'action', action: aim.action }, true);
    if (sim.turn.phase !== 'aim') return false;
    sim.applyInput(unit.playerId, { kind: 'fire', angle: aim.angle, power: aim.power }, true);
    if (sim.turn.phase === 'aim') {
      sim.applyInput(unit.playerId, { kind: 'cancel' }, true);
      return false;
    }
    return true;
  }

  /**
   * The aim that lands a catapult ride near the target: the flight is searched like a throw from
   * the cup with the catapult's own speed and the moles' gravity, and a landing in the water or
   * off the map is no good (the mole would be the one lost).
   */
  private rideAim(sim: Sim, unit: Unit, target: Unit, facing: 1 | -1, station: Catapult): { angle: number; power: number } | null {
    const best = this.searchShot(sim, unit, target, facing, 'catapult', { x: station.x + facing * 6, y: station.y - CATAPULT_CUP_H });
    if (best.miss > 260) return null;
    const land = this.simulate(sim, unit, best.angle, facing, launchSpeed(ACTIONS.catapult, best.power, 1), ACTIONS.catapult, { x: station.x + facing * 6, y: station.y - CATAPULT_CUP_H });
    const deathY = sim.level.waterY ?? sim.level.height;
    if (!land || land.y > deathY - 30) return null;
    return { angle: best.angle, power: best.power };
  }

  /**
   * Where a blink would put the mole on the way to its target: the standing spots in the column
   * halfway there (or as far as the teleport reaches), the one nearest the target's height. The
   * aim is worked back from the spot and checked the way the sim will check it. Null when
   * nothing there is clear, dry and within reach.
   */
  private blinkAim(sim: Sim, unit: Unit, target: Unit, facing: 1 | -1): { angle: number; power: number } | null {
    const probe: Unit = { ...unit, facing };
    const isSolid = (x: number, y: number): boolean => sim.isSolid(x, y);
    const deathY = sim.level.waterY ?? sim.level.height;
    let best: { angle: number; power: number; score: number } | null = null;
    for (const frac of [0.5, 0.4, 0.6]) {
      const out = Math.min(Math.abs(target.x - unit.x) * frac, TELEPORT_REACH * 0.95);
      if (out < 120) continue;
      const hx = clamp(unit.x + facing * out, UNIT_HALF_W + 2, sim.level.width - UNIT_HALF_W - 2);
      let above = isSolid(hx, UNIT_H + 4);
      for (let y = UNIT_H + 6; y < deathY - 2; y += 2) {
        const solid = isSolid(hx, y);
        if (solid && !above) {
          const feet = y - 1;
          const fx = (hx - unit.x) * facing;
          const fy = unit.y - feet;
          const reach = Math.hypot(fx, fy);
          if (reach <= TELEPORT_REACH && reach >= TELEPORT_REACH * 0.12) {
            const angle = Math.atan2(fy, fx);
            const power = reach / TELEPORT_REACH;
            const spot = teleportSpot(sim.level, isSolid, probe, angle, power);
            if (spot.ok && Math.abs(spot.y - feet) < 4) {
              const score = Math.abs(feet - target.y) + Math.abs(frac - 0.5) * 400;
              if (!best || score < best.score) best = { angle, power, score };
            }
          }
        }
        above = solid;
      }
    }
    return best ? { angle: best.angle, power: best.power } : null;
  }

  /** Where to point a straight shot at a mole: the chest, or the head when a confident shot feels lucky. */
  private hitscanAim(unit: Unit, at: Unit, err: number): number {
    const aimY = at.y - UNIT_H * (this.difficulty > 0.6 && this.rng() < 0.5 ? 0.85 : 0.5);
    const angle = Math.atan2(unit.y - UNIT_H * 0.62 - aimY, Math.abs(at.x - unit.x));
    return clamp(angle + (this.rng() - 0.5) * err * 0.5, -Math.PI / 2, Math.PI / 2);
  }

  /**
   * Call the airstrike down on a mole. The mark is dragged straight out (angle 0) as far as the
   * mole is, less the drift the bombs pick up falling the way we face, so the middle bomb lands
   * on it. Unless `always`, null when a roof or a ceiling would catch the bombs, or when the mole
   * is close enough that the line of bombs would reach us too.
   */
  private strikeAt(sim: Sim, unit: Unit, target: Unit, facing: 1 | -1, always: boolean): Attack | null {
    if (!always && (Math.abs(target.x - unit.x) < 260 || this.roofed(sim, target))) return null;
    // The middle bomb starts 130 px above the map falling at 5 px a tick and drifts 0.6 px a tick.
    const g = ACTIONS.airstrike.gravity * sim.level.gravity;
    const fall = target.y - UNIT_H / 2 + 130;
    const ticks = (Math.sqrt(25 + 2 * g * fall) - 5) / g;
    const mark = target.x - facing * 0.6 * ticks;
    const power = clamp(Math.abs(mark - unit.x) / STRIKE_REACH, 0.05, 1);
    return { action: 'airstrike', angle: 0, power, facing, good: true };
  }

  /** Something solid hangs over this mole within 400 px (a ledge, an upper deck, a plank): bombs from the sky would land on that instead. */
  private roofed(sim: Sim, u: Unit): boolean {
    for (let dy = UNIT_H + 4; dy <= 400; dy += 8) if (sim.isSolid(u.x, u.y - dy)) return true;
    return false;
  }

  /**
   * Set the charge down at the feet of a mole just out of reach: the aim is worked back from a
   * spot a step in front of them (inside their body the sim refuses it) and checked the way the
   * sim will check it. Null when the spot is out of reach, refused, or close enough that the
   * blast would catch us too.
   */
  private chargeAt(sim: Sim, unit: Unit, target: Unit, facing: 1 | -1): Attack | null {
    const def = ACTIONS.tnt;
    const sx = target.x - facing * 40;
    const sy = target.y - 14;
    if (Math.hypot(sx - unit.x, sy - (unit.y - UNIT_H / 2)) < def.radius + UNIT_HALF_W + 14) return null;
    const fx = (sx - unit.x) * facing;
    const fy = unit.y - UNIT_H * 0.7 - sy;
    const reach = Math.hypot(fx, fy);
    if (fx <= 0 || reach > PLACE_REACH) return null;
    const angle = Math.atan2(fy, fx);
    const power = reach / PLACE_REACH;
    const spot = platformSpot(sim.level, sim.units, { ...unit, facing }, angle, power, 24);
    if (!spot.ok || Math.hypot(spot.x - target.x, spot.y - target.y) > 60) return null;
    return { action: 'tnt', angle, power, facing, good: true };
  }

  private chooseAttack(sim: Sim, unit: Unit): Attack {
    const target = this.nearestEnemy(sim, unit);
    const facing: 1 | -1 = target && target.x < unit.x ? -1 : 1;
    const skip = (good: boolean): Attack => ({ action: 'skip', angle: 0.6, power: 0.6, facing, good });
    if (!target) return skip(true);
    const team = sim.teamOf(unit.team);
    const ammo = (w: WeaponId) => (this.refused.has(w) ? 0 : team?.ammo[w] ?? 0);
    const dist = Math.hypot(target.x - unit.x, target.y - unit.y);
    const err = (1 - this.difficulty) * 0.3;
    const seen = this.visibleEnemy(sim, unit);
    const los = seen !== null;
    const seenDist = seen ? Math.hypot(seen.x - unit.x, seen.y - unit.y) : Infinity;
    // A straight shot at the mole we can see (or, for the railgun, at one we cannot).
    const gunAt = (weapon: ActionId, at: Unit = seen ?? target): Attack => ({
      action: weapon, angle: this.hitscanAim(unit, at, err), power: 1, facing: at.x < unit.x ? -1 : 1, good: true,
    });
    const hitsWith = (weapon: ActionId, origin?: { x: number; y: number }): Attack & { miss: number } => {
      const def = ACTIONS[weapon];
      const best = this.searchShot(sim, unit, target, facing, weapon, origin);
      // Every option lands on our own toes (down a crater, say) or on a friend: hold fire this turn.
      if (best.selfHit) return { ...skip(false), miss: Infinity };
      // A blast counts when it lands inside its radius; a bolt or a ball only when it all but touches.
      const good = def.radius > 0 ? best.miss < def.radius * 0.9 : best.miss < 24;
      // A hard bouncer goes wherever a hair's difference sends it: those are thrown exactly as searched.
      const wobble = (def.bounce ?? 0) >= 0.6 ? 0 : err;
      let angle = clamp(best.angle + (this.rng() - 0.5) * wobble, -Math.PI / 2, Math.PI / 2);
      let power = clamp(best.power + (this.rng() - 0.5) * wobble * 0.6, 0.15, 1);
      // The wobble must not turn a near thing into a blast on our own side (a lob that just clears a team-mate's head, say).
      if (wobble > 0 && this.outcome(sim, unit, target, facing, weapon, angle, power, origin)?.selfHit) {
        angle = best.angle;
        power = best.power;
      }
      return { action: weapon, angle, power, facing, good, miss: best.miss };
    };

    const only = this.options.onlyWeapon;
    if (only) {
      if (ammo(only) <= 0) return skip(true);
      const def = ACTIONS[only];
      if (def.kind === 'hitscan') {
        // A piercing slug needs no line: straight at the nearest mole through whatever is between.
        if (def.pierce) return gunAt(only, target);
        // No line of sight (or out of a short gun's reach): don't waste a round on the scenery.
        const inRange = los && (!def.range || seenDist <= def.range * 0.95);
        return inRange ? gunAt(only) : skip(false);
      }
      if (def.kind === 'melee') {
        return sim.meleeTarget(unit, def.reach ?? 64) ? { action: only, angle: 0, power: 1, facing, good: true } : skip(false);
      }
      // The sky is called down every turn, roof or no roof: the balance rule wants the thing used.
      if (def.strike) return this.strikeAt(sim, unit, target, facing, true) ?? skip(false);
      if (def.place === 'charge') return this.chargeAt(sim, unit, target, facing) ?? skip(false);
      if (def.place || def.dig) return skip(true);
      return hitsWith(only);
    }

    // Someone in reach: swing the best thing we carry (the bat over water, the mallet for damage), else boot.
    for (const w of (sim.level.waterY !== null ? ['bat', 'mallet', 'pike', 'boot'] : ['mallet', 'bat', 'pike', 'boot']) as WeaponId[]) {
      if (ammo(w) > 0 && sim.meleeTarget(unit, ACTIONS[w].reach ?? 64)) return { action: w, angle: 0, power: 1, facing, good: true };
    }

    const style = this.options.style ?? 'balanced';
    const dx = Math.abs(target.x - unit.x);
    // A charge set at the feet of someone just past arm's length (and just past its own blast).
    if (ammo('tnt') > 0 && dx >= 170 && dx <= 300 && this.rng() < 0.8) {
      const charge = this.chargeAt(sim, unit, target, facing);
      if (charge) return charge;
    }
    // Up close with a clear line, the shotgun is a boot with reach, and the SMG's burst forgives a shaky aim.
    if (los && ammo('shotgun') > 0 && seenDist < (ACTIONS.shotgun.range ?? 340) * 0.8) return gunAt('shotgun');
    if (los && ammo('smg') > 0 && seenDist < 450 && this.rng() < 0.7) return gunAt('smg');
    // Over water, a harpoon drags them into the drink.
    if (los && ammo('harpoon') > 0 && sim.level.waterY !== null && seenDist < (ACTIONS.harpoon.range ?? 1000) && this.rng() < 0.6) return gunAt('harpoon');
    // Sniper when we have a clear line (the railgun is a sniper that also goes through things); the free pistol only up close.
    const sniperUrge = style === 'sniper' ? 0.95 : 0.5 + this.difficulty * 0.4;
    if (los && ammo('sniper') > 0 && this.rng() < sniperUrge) return gunAt('sniper');
    if (los && ammo('railgun') > 0 && ammo('sniper') <= 0 && this.rng() < sniperUrge * 0.7) return gunAt('railgun');
    // No line to the nearest mole (a hill, a hull, a wall of sandbags): the railgun does not care.
    if (ammo('railgun') > 0 && (!los || seen !== target) && this.rng() < 0.8) return gunAt('railgun', target);
    // A bolt flies nearly flat and only hurts what it hits: worth it with a clear line at middling range.
    if (los && ammo('crossbow') > 0 && seenDist < 700 && this.rng() < 0.6) {
      const bolt = hitsWith('crossbow');
      if (bolt.good) return bolt;
    }
    if (los && dist < 420 && ammo('grenade') <= 0 && this.rng() < 0.6) return gunAt('gun');

    // Bombs from the sky: nothing to throw over, so a plateau or the far side of a hill is as open as a field.
    const strike = ammo('airstrike') > 0 ? this.strikeAt(sim, unit, target, facing, false) : null;
    const high = target.y < unit.y - 110;
    if (strike && this.rng() < (high || dist > 900 ? 0.7 : 0.2)) return strike;

    const preferBall = sim.level.waterY !== null && dist < 420 && this.rng() < (style === 'rusher' ? 0.6 : 0.4);
    let weapon: ActionId = 'ball';
    // The massive bomb only at a distance: its crater would swallow the thrower too (searchShot keeps it 300 px off).
    if (ammo('massive') > 0 && dist >= 450 && this.rng() < (style === 'bomber' ? 0.6 : 0.4)) weapon = 'massive';
    else if (ammo('rpg') > 0 && this.rng() < (style === 'bomber' ? 0.7 : 0.5)) weapon = 'rpg';
    else if (ammo('mortar') > 0 && this.rng() < 0.6) weapon = 'mortar';
    else if (ammo('bignade') > 0 && this.rng() < 0.6) weapon = 'bignade';
    else if (ammo('sticky') > 0 && this.rng() < 0.55) weapon = 'sticky';
    // The rubber bomb wants walls and floors to work with; over water it mostly bounces into the drink.
    else if (ammo('rubber') > 0 && sim.level.waterY === null && this.rng() < 0.45) weapon = 'rubber';
    else if (ammo('cluster') > 0 && this.rng() < 0.5) weapon = 'cluster';
    else if (ammo('mine') > 0 && this.rng() < 0.3) weapon = 'mine';
    else if (ammo('grenade') > 0 && !preferBall) weapon = 'grenade';
    const shot = hitsWith(weapon);
    if (shot.good) return shot;
    // A lob that will not land: a grenade may still get there where a ball would not; failing
    // that, the sky, the railgun or a pistol, which do not care what is in the way.
    if (weapon !== 'grenade' && ammo('grenade') > 0) {
      const alt = hitsWith('grenade');
      if (alt.good) return alt;
    }
    if (strike) return strike;
    if (ammo('railgun') > 0) return gunAt('railgun', target);
    if (los && dist < 700) return gunAt('gun');
    return shot;
  }

  /** The closest enemy this unit can draw a straight line to, if any. */
  private visibleEnemy(sim: Sim, unit: Unit): Unit | null {
    const enemies = sim.units.filter((u) => u.alive && u.team !== unit.team && this.lineOfSight(sim, unit, u));
    enemies.sort((a, b) => Math.hypot(a.x - unit.x, a.y - unit.y) - Math.hypot(b.x - unit.x, b.y - unit.y));
    return enemies[0] ?? null;
  }

  private lineOfSight(sim: Sim, a: Unit, b: Unit): boolean {
    const x0 = a.x;
    const y0 = a.y - UNIT_H * 0.62;
    const x1 = b.x;
    const y1 = b.y - UNIT_H * 0.5;
    const steps = Math.ceil(Math.hypot(x1 - x0, y1 - y0) / 4);
    // Anyone else standing in the line (a team-mate in front, say) would take the round instead.
    const others = sim.units.filter((u) => u.alive && u !== a && u !== b);
    for (let i = 1; i < steps; i++) {
      const t = i / steps;
      const x = x0 + (x1 - x0) * t;
      const y = y0 + (y1 - y0) * t;
      if (sim.isSolid(x, y)) return false;
      for (const u of others) if (Math.abs(x - u.x) <= 16 && y <= u.y && y >= u.y - UNIT_H) return false;
    }
    return true;
  }

  /**
   * The aim (angle, power) whose predicted landing comes closest to the target, from a coarse
   * grid and a few random refinements around the best cell. `origin` is where the thing leaves
   * from when it is not the mole's hand (a catapult's cup).
   */
  private searchShot(
    sim: Sim, unit: Unit, target: Unit, facing: 1 | -1, weapon: ActionId, origin?: { x: number; y: number },
  ): { angle: number; power: number; miss: number; selfHit: boolean } {
    const def = ACTIONS[weapon];
    let best = { angle: 0.8, power: 0.7, score: Infinity, miss: Infinity, selfHit: false };
    const consider = (angle: number, power: number): void => {
      const end = this.outcome(sim, unit, target, facing, weapon, angle, power, origin);
      if (!end) return;
      const score = end.miss + (end.selfHit ? 600 : 0);
      if (score < best.score) best = { angle, power, score, miss: end.miss, selfHit: end.selfHit };
    };
    // Lobs from flat up to nearly straight up; when the target is well below us (the foot of a
    // cliff, a pit) a throw dropped over the edge beats any arc, so the comb reaches down too.
    const low = -(target.y > unit.y + 80 ? 1.2 : 0.1);
    // A hard bouncer's resting place jumps around with the smallest change of aim, so it gets a finer comb.
    const bouncy = (def.bounce ?? 0) >= 0.6;
    const angles = (bouncy ? 18 : 14) + (low < -0.5 ? 8 : 0);
    const powers = bouncy ? 12 : 10;
    for (let ai = 0; ai < angles; ai++) {
      const angle = low + (ai / (angles - 1)) * (1.5 - low);
      for (let pi = 0; pi < powers; pi++) consider(angle, 0.15 + (pi / (powers - 1)) * 0.85);
    }
    // Bouncing grenades land chaotically, so look around the best cell for something tighter.
    for (let i = 0; i < (bouncy ? 40 : 24) && best.miss > 12; i++) {
      const angle = clamp(best.angle + (this.rng() - 0.5) * 0.12, low - 0.1, 1.5);
      const power = clamp(best.power + (this.rng() - 0.5) * 0.1, 0.15, 1);
      consider(angle, power);
    }
    return { angle: best.angle, power: best.power, miss: best.miss, selfHit: best.selfHit };
  }

  /**
   * How one aim would turn out: how far from the target's middle the thing ends (zero when it
   * ends on the target itself), and whether it would catch our own side. Our own toes get the
   * blast radius plus a margin (the massive bomb more: its crater swallows the thrower), and a
   * team-mate must be neither touched nor stood inside the blast. Null when the throw goes nowhere.
   */
  private outcome(
    sim: Sim, unit: Unit, target: Unit, facing: 1 | -1, weapon: ActionId, angle: number, power: number, origin?: { x: number; y: number },
  ): { miss: number; selfHit: boolean } | null {
    const def = ACTIONS[weapon];
    const mult = sim.teamOf(unit.team)?.perks.throwMultiplier ?? 1;
    const land = this.simulate(sim, unit, angle, facing, launchSpeed(def, power, mult), def, origin);
    if (!land) return null;
    const miss = land.hit === target ? 0 : Math.hypot(land.x - target.x, land.y - (target.y - UNIT_H / 2));
    const keepOut = Math.max(def.radius + 40, weapon === 'massive' ? 300 : 0);
    let selfHit = Math.hypot(land.x - unit.x, land.y - (unit.y - UNIT_H / 2)) < keepOut;
    for (const u of sim.units) {
      if (selfHit || !u.alive || u === unit || u.team !== unit.team) continue;
      if (land.hit === u || Math.hypot(land.x - u.x, land.y - (u.y - UNIT_H / 2)) < def.radius * 0.8 + UNIT_HALF_W) selfHit = true;
    }
    return { miss, selfHit };
  }

  /** The mole whose body covers (x, y), the way the sim checks it; `except` is skipped. */
  private unitAt(sim: Sim, x: number, y: number, except: Unit | null): Unit | null {
    for (const u of sim.units) {
      if (!u.alive || u === except) continue;
      if (Math.abs(x - u.x) <= UNIT_HALF_W && y <= u.y && y >= u.y - UNIT_H) return u;
    }
    return null;
  }

  /**
   * Where a throw ends up, by the sim's own rules: grenade-like things bounce (off moles too,
   * the rubber bomb hard) until their fuse runs out, a sticky bites the first thing it touches,
   * a cluster pops on touchdown, and the rest bursts on contact. `hit` is the mole it ended on,
   * if any. A boulder leaves from the catapult's cup (`origin`) rather than the hand.
   */
  private simulate(
    sim: Sim, unit: Unit, angle: number, facing: 1 | -1, speed: number, def: ActionDef, origin?: { x: number; y: number },
  ): { x: number; y: number; hit: Unit | null } | null {
    const dirX = Math.cos(angle) * facing;
    const dirY = -Math.sin(angle);
    let x = origin ? origin.x : unit.x + facing * 10 + dirX * 24;
    let y = origin ? origin.y : unit.y - UNIT_H * 0.78 + dirY * 24;
    let vx = dirX * speed;
    let vy = dirY * speed;
    const fuse = def.fuseTicks ?? 0;
    // The sim sets a fused thing off at the top of the tick its fuse hits zero, so it moves one tick fewer than the fuse says.
    const maxT = fuse > 0 ? fuse - 1 : 420;
    const offMoles = def.id === 'rubber' ? 0.8 : def.id === 'grenade' || def.id === 'bignade' || def.id === 'mine' ? 0.35 : 0;
    // Wind only leans on the lobbed things, not on rockets, bolts or bombs from the sky.
    const windy = sim.settings.wind && def.gravity >= 0.25 && def.speed > 0;
    let last = { x, y };
    for (let t = 0; t < maxT; t++) {
      if (windy) vx += sim.wind * 0.04;
      vy += def.gravity * sim.level.gravity;
      const steps = Math.max(1, Math.ceil(Math.hypot(vx, vy) / 3));
      for (let s = 0; s < steps; s++) {
        const nx = x + vx / steps;
        const ny = y + vy / steps;
        if (nx < -60 || nx > sim.level.width + 60 || ny > sim.level.height + 60) return fuse > 0 ? { ...last, hit: null } : null;
        if (sim.level.waterY !== null && ny > sim.level.waterY) return { x: nx, y: ny, hit: null };
        if (def.bounce !== undefined && sim.stopBlocks(x, y, nx)) {
          vx = -vx * 0.4;
          break;
        }
        // The sim lets a throw clear its own thrower for its first few ticks; after that it is a mole like any other.
        const hit = this.unitAt(sim, nx, ny, t < 9 ? unit : null);
        if (hit) {
          if (offMoles > 0) {
            vx = -vx * offMoles;
            vy = -Math.abs(vy) * offMoles;
            break;
          }
          return { x: nx, y: ny, hit };
        }
        if (sim.isSolid(nx, ny)) {
          if (def.stick || def.split) return { x, y, hit: null };
          if (fuse <= 0 || def.bounce === undefined) return { x: nx, y: ny, hit: null };
          const r = sim.reflect(nx, ny, vx, vy, def.bounce);
          vx = r.vx;
          vy = r.vy;
          break;
        }
        x = nx;
        y = ny;
        last = { x, y };
      }
    }
    return fuse > 0 ? { ...last, hit: null } : null;
  }
}
